<template>
  <component
    :is="tag"
    class="fts-form-label"
    :class="[
      {'with-icon': icon},
      {'form-label--top': placement === 'top'},
      {'form-label--left': placement === 'left'},
      {'form-label--icon-right': iconRight},
      {'form-label--nowrap': !textWrap},
      {'form-label--comfort': comfortDesign},
      {'form-label--required': required},
      {'form-label--error': error},
      {'form-label--py': maintainLabelHeight}
    ]"
  >
    <!-- icon -->
    <div class="form-label__icon" v-if="icon || Boolean($slots.icon) || Boolean($scopedSlots.icon)">
      <slot name="icon">
        <i :class="icon"></i>
      </slot>
    </div>
    <!-- label -->
    <div class="form-label__label">
      <slot />
    </div>
    <!-- trailing controls, after the type icon: label > icon > suffix, in both flex directions
         (see the `order` rules below). Added for SS05971's freeze indicator; renders nothing at
         all when no suffix slot is supplied. -->
    <div class="form-label__suffix" v-if="Boolean($slots.suffix)">
      <slot name="suffix" />
    </div>
  </component>
</template>

<script>
export default {
  props: {
    tag: {
      type: String,
      default: 'label'
    },
    icon: String,
    iconRight: Boolean,
    comfortDesign: Boolean,
    required: Boolean,
    error: Boolean,
    placement: String,
    textWrap: {
      type: Boolean,
      default: true
    },
    maintainLabelHeight: Boolean // work with form-control
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-form-label {
  display: flex;
	align-items: flex-start;
	font-family: $font-family;
	font-weight: 400;
  font-weight: var(--inistate-font-weight-light);
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
	color: var(--fts-form-label);
  .form-label__icon {
    display: inline-flex;
    margin-right: 0.375rem;
    [class^="fts-icon-"],
    [class*=" fts-icon-"] {
      line-height: inherit;
    }
  }
  .form-label__label {
    width: inherit;
    word-break: normal;
    overflow-wrap: anywhere;
  }
  /* Sequence is label > type icon > suffix.
     `order` rather than DOM position, because the icon is the FIRST child in markup and
     `form-label--icon-right` renders the row reversed — so the visually-last slot is the
     highest order in one direction and the lowest in the other. `flex: none` keeps the suffix
     out of the label's width, so `form-label--nowrap`'s ellipsis still belongs to the label. */
  .form-label__suffix {
    flex: none;
    display: inline-flex;
    align-items: center;
    order: 1;
    /* A slot that renders nothing must cost nothing. `$slots.suffix` is truthy as soon as a
       caller passes a component, even one whose own `v-if` renders it away — SS05971's chip is
       exactly that. Vue leaves a comment node behind, which `:empty` ignores, so this collapses
       the wrapper rather than leaving a gap in the label row (AC30). Spacing therefore belongs
       to the slotted content, not here. */
    &:empty {
      display: none;
    }
  }
  &.form-label--top {
    min-width: 0;
    margin-bottom: 0.375rem;
    &:not(.form-label--icon-right) {
      width: 100%;
    }
    & + .fts-form-helper {
      margin-top: -0.25rem;
    }
  }
  &.form-label--icon-right {
    flex-direction: row-reverse;
    justify-content: flex-end;
    .form-label__icon {
      margin-left: 0.375rem;
      margin-right: 0;
    }
    /* row-reverse walks the main axis right-to-left, so the LOWEST order sits rightmost —
       which is where the suffix has to stay. `form-label--left` @extends this block, so it is
       covered too. */
    .form-label__suffix {
      order: -1;
    }
  }
  &.form-label--left {
    width: 140px;
    min-width: 140px;
    max-width: 140px;
    margin-right: 0.375rem;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border-radius: $border-radius;
    .form-label__label {
      text-align: left;
    }
  }
  &.form-label--py {
    &.form-label--top {
      margin-bottom: 0;
    }
  }
  &.form-label--left,
  &.form-label--py {
    padding-top: calc(0.375rem + 1px);
    padding-bottom: calc(0.375rem + 1px);
  }
  &.form-label--required {
    .form-label__icon {
      color: var(--fts-danger);
    }
  }
  &.form-label--error {
    color: var(--fts-danger);
  }
  &.form-label--nowrap {
    .form-label__label {
      @include text-overflow-ellipsis;
    }
  }
}
</style>